import React, { useRef, useEffect, useState } from "react";
import TitleStripe from "../TitleStripe";

function AnimatedBar({ pct, colorHex }) {
  const [displayPct, setDisplayPct] = useState(0);
  const requestRef = useRef();

  useEffect(() => {
    let start = null;
    let initial = displayPct;
    let target = pct;
    let duration = 900;
    function animate(ts) {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setDisplayPct(Math.round(initial + (target - initial) * progress));
      if (progress < 1) {
        requestRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayPct(target);
      }
    }
    requestRef.current && cancelAnimationFrame(requestRef.current);
    requestRef.current = requestAnimationFrame(animate);
    return () => requestRef.current && cancelAnimationFrame(requestRef.current);
    // eslint-disable-next-line
  }, [pct]);

  return (
    <div className="w-full select-none">
      <style>{`
        @keyframes barShimmer {
          0%   { transform: translateX(-100%) skewX(-12deg); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateX(350%) skewX(-12deg); opacity: 0; }
        }
        @keyframes barGlow {
          0%, 100% { opacity: 0.45; }
          50%       { opacity: 1; }
        }
      `}</style>

      {/* Porcentagem em destaque */}
      <div className="flex items-end justify-between mb-3">
        <div className="flex items-baseline gap-1">
          <span
            className="text-4xl font-black tabular-nums leading-none"
            style={{ color: colorHex }}
          >
            {displayPct}
          </span>
          <span className="text-xl font-bold" style={{ color: colorHex }}>
            %
          </span>
        </div>
        <span className="text-xs text-gray-400 font-medium pb-0.5">
          de 100%
        </span>
      </div>

      {/* Trilha com zonas de tier */}
      <div
        className="relative h-5 w-full rounded-full overflow-hidden bg-gray-100"
        style={{ boxShadow: "inset 0 2px 4px rgba(0,0,0,0.08)" }}
      >
        {/* Zonas coloridas de fundo */}
        <div className="absolute inset-0 flex">
          <div
            className="h-full opacity-20"
            style={{ width: "25%", background: "#dc2626" }}
          />
          <div
            className="h-full opacity-20"
            style={{ width: "25%", background: "#3b82f6" }}
          />
          <div
            className="h-full opacity-20"
            style={{ width: "25%", background: "#f59e0b" }}
          />
          <div
            className="h-full opacity-20"
            style={{ width: "25%", background: "#22c55e" }}
          />
        </div>
        {/* Divisores entre zonas */}
        {[25, 50, 75].map((pos) => (
          <div
            key={pos}
            className="absolute top-0 bottom-0 w-px bg-white/70 z-10"
            style={{ left: `${pos}%` }}
          />
        ))}
        {/* Barra preenchida */}
        <div
          className="absolute left-0 top-0 h-full rounded-full overflow-hidden"
          style={{
            width: `${displayPct}%`,
            background: `linear-gradient(90deg, ${colorHex}99 0%, ${colorHex} 100%)`,
            transition: "width 1s cubic-bezier(.22,1.2,.36,1)",
          }}
        >
          {/* Brilho superior fixo */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/35 to-transparent pointer-events-none" />
          {/* Shimmer sweep contínuo */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "35%",
              height: "100%",
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)",
              animation: "barShimmer 2.2s ease-in-out infinite",
            }}
          />
        </div>

        {/* Glow pulsante externo */}
        {displayPct > 0 && (
          <div
            className="absolute top-0 h-full rounded-full pointer-events-none"
            style={{
              left: 0,
              width: `${displayPct}%`,
              boxShadow: `0 0 10px 4px ${colorHex}44`,
              animation: "barGlow 2s ease-in-out infinite",
              transition: "width 1s cubic-bezier(.22,1.2,.36,1)",
            }}
          />
        )}
      </div>

      {/* Escala */}
      <div className="flex justify-between mt-1.5">
        {["0%", "25%", "50%", "75%", "100%"].map((label) => (
          <span key={label} className="text-[10px] text-gray-400 font-medium">
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function PontualidadePagamentoPJQuery({
  data,
  periodLabel = "Últimos 12 meses",
  className = "",
  layout = "full",
}) {
  if (data?.indicador == null) {
    return (
      <div
        className={`w-full rounded-md border border-gray-200 p-5 mt-4 ${className}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <TitleStripe>Pontualidade de Pagamentos</TitleStripe>
            <p className="mt-0.5 text-sm text-gray-500">
              {periodLabel
                ? `Período: ${periodLabel}`
                : "Indicador indisponível"}
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 mt-4">
          <div className="flex gap-3">
            <span className="mt-1 h-2.5 w-2.5 flex-none rounded-full bg-amber-500" />
            <div className="text-sm leading-relaxed text-amber-900">
              Indicador indisponível para este período.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const raw = Number(data?.indicador);
  const v = Number.isFinite(raw) ? clamp(raw, 0, 1) : 0;
  const pct = Math.round(v * 100);

  const tier = getTier(v);
  const { colorHex } = tierColors(tier);

  const isHalf = layout === "half";

  return (
    <div className={`w-full ${className}`}>
      <div className="">
        <div>
          <TitleStripe>Pontualidade de Pagamentos</TitleStripe>
          <p className="mt-2 text-sm text-gray-500">
            Percentual de boletos pagos sobre os boletos emitidos •{" "}
            {periodLabel}
          </p>
        </div>

        <div
          className={
            isHalf
              ? "mt-6 flex flex-col gap-4 items-stretch"
              : "mt-6 grid grid-cols-2 gap-8 items-center"
          }
        >
          <div
            className={
              isHalf
                ? "flex flex-col items-center justify-center gap-2"
                : "flex items-center justify-start gap-6"
            }
          >
            <div
              className={
                isHalf ? "w-full max-w-[460px]" : "w-full max-w-[480px]"
              }
            >
              <AnimatedBar pct={pct} colorHex={colorHex} />
            </div>
          </div>

          <div
            className={
              isHalf ? "w-full text-center" : "min-w-[220px] text-left"
            }
          >
            <div className="border border-gray-200 bg-gray-50 p-3 rounded-md ">
              <span className="block text-base text-gray-800 font-medium">
                A empresa está classificada como{" "}
                <span style={{ color: colorHex, fontWeight: 700 }}>
                  {tier.charAt(0).toUpperCase() + tier.slice(1)}
                </span>
              </span>
              <span className="block text-sm text-gray-600 mt-1">
                Pagou {pct}% dos seus compromissos nos últimos 12 meses.
              </span>
            </div>
            <div className="flex flex-wrap gap-4 mt-3">
              <LegendItem cor="#dc2626" texto="0-25% Mínima" />
              <LegendItem cor="#3b82f6" texto="26-50% Baixa" />
              <LegendItem cor="#f59e0b" texto="51-75% Média" />
              <LegendItem cor="#22c55e" texto="76-100% Alta" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LegendItem({ cor, texto }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-block w-4 h-4 rounded-full border border-gray-300"
        style={{ background: cor }}
      />
      <span className="text-xs text-gray-700">{texto}</span>
    </div>
  );
}

function ContextMessage({ tier, pct, compact = false }) {
  const content =
    tier === "excelente" ? (
      <p>
        Liquidez <strong>excelente</strong> ({pct}%). A adimplência via boleto
        está em nível muito saudável.
      </p>
    ) : tier === "boa" ? (
      <p>
        Liquidez <strong>boa</strong> ({pct}%). Acompanhe a evolução e mantenha
        as políticas atuais.
      </p>
    ) : tier === "atenção" ? (
      <p>
        Liquidez em <strong>atenção</strong> ({pct}%). Reveja prazos, lembretes
        e condições para reduzir inadimplência.
      </p>
    ) : (
      <p>
        Liquidez <strong>crítica</strong> ({pct}%). Indica alto nível de
        inadimplência; avalie ações de cobrança e revisão de crédito.
      </p>
    );

  return (
    <div
      className={`rounded-2xl border p-4 ${
        compact ? "text-sm" : ""
      } ${messageBorderClass(tier)}`}
    >
      <div className="flex gap-3">
        <span
          className={`mt-1 h-2.5 w-2.5 flex-none rounded-full ${messageDotClass(
            tier,
          )}`}
        />
        <div className="leading-relaxed text-gray-700">{content}</div>
      </div>
    </div>
  );
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function getTier(v) {
  const p = v * 100;
  if (p <= 25) return "mínima";
  if (p <= 50) return "baixa";
  if (p <= 75) return "média";
  return "alta";
}

function tierColors(tier) {
  switch (tier) {
    case "alta":
      return { colorHex: "#22c55e" };
    case "média":
      return { colorHex: "#f59e0b" };
    case "baixa":
      return { colorHex: "#3b82f6" };
    case "mínima":
    default:
      return { colorHex: "#dc2626" };
  }
}

function messageBorderClass(tier) {
  switch (tier) {
    case "excelente":
      return "border-green-200 bg-green-50";
    case "boa":
      return "border-blue-200 bg-blue-50";
    case "atenção":
      return "border-amber-200 bg-amber-50";
    case "crítica":
    default:
      return "border-red-200 bg-red-50";
  }
}

function messageDotClass(tier) {
  switch (tier) {
    case "excelente":
      return "bg-green-500";
    case "boa":
      return "bg-blue-500";
    case "atenção":
      return "bg-amber-500";
    case "crítica":
    default:
      return "bg-red-600";
  }
}
